'use client';

import { motion, useInView } from 'framer-motion';
import { useRef } from 'react';

export default function SolutionSection() {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-50px' });

  const benefits = [
    {
      icon: 'ri-battery-charge-line',
      title: 'Never Dies On You',
      description: 'USB rechargeable battery lasts weeks per charge. No more fuel, no more dead lighters at the worst times.'
    },
    {
      icon: 'ri-shield-line',
      title: 'Military-Grade Durability',
      description: 'Tactical aluminum construction survives drops, weather, and daily punishment. Built to last years, not weeks.'
    },
    {
      icon: 'ri-windy-line',
      title: 'Windproof Performance',
      description: 'Works in rain, snow, and heavy wind. The flame stays lit when cheap lighters give up.'
    },
    {
      icon: 'ri-archive-line',
      title: 'Protects Your Cigarettes',
      description: 'Built-in case keeps smokes fresh, unbroken, and ready. No more crushed cigarettes in your pocket.'
    },
    {
      icon: 'ri-temp-cold-line',
      title: 'All-Weather Reliability',
      description: 'Functions perfectly in extreme cold and heat. Your backup when everything else fails.'
    },
    {
      icon: 'ri-briefcase-line',
      title: 'Professional Look',
      description: 'Premium design that actually looks expensive. People notice quality when they see it.'
    },
    {
      icon: 'ri-flashlight-line',
      title: 'Instant Ignition',
      description: 'Push-button operation with immediate flame. No clicking, no wheel spinning, no frustration.'
    },
    {
      icon: 'ri-lock-2-line',
      title: 'Safety Features',
      description: 'Child-resistant design with secure closure. Responsible engineering for peace of mind.'
    }
  ];

  return (
    <section ref={ref} className="py-12 sm:py-16 md:py-24 lg:py-32 bg-white">
      <div className="max-w-7xl mx-auto px-4">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
          className="text-center mb-8 sm:mb-10 md:mb-16"
        >
          <h2 className="font-serif text-2xl sm:text-3xl md:text-4xl lg:text-5xl text-black mb-3 sm:mb-4 md:mb-6 leading-tight">
            Why This Changes Everything
          </h2>
          <p className="text-black/70 text-sm sm:text-base md:text-lg max-w-3xl mx-auto leading-relaxed">
            Eight game-changing features that make this the only lighter you'll ever need
          </p>
        </motion.div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 md:gap-8">
          {benefits.map((benefit, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 30 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, delay: 0.1 + index * 0.1 }}
              className="bg-black/5 backdrop-blur-sm border border-black/10 rounded-2xl p-5 sm:p-6 md:p-8 hover:bg-black/10 hover:border-black/20 transition-all duration-300 cursor-default"
            >
              <div className="w-12 h-12 sm:w-14 sm:h-14 md:w-16 md:h-16 flex items-center justify-center bg-black rounded-xl mb-4 sm:mb-5">
                <i className={`${benefit.icon} text-2xl sm:text-3xl md:text-4xl text-white`} />
              </div>
              <h3 className="text-black font-bold text-base sm:text-lg md:text-xl mb-2 sm:mb-3 leading-snug">
                {benefit.title}
              </h3>
              <p className="text-black/70 text-xs sm:text-sm md:text-base leading-relaxed">
                {benefit.description}
              </p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
